/**
 * External Imports
 */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
/**
 * Nexus Imports
 */
import { NeuButton, NeuIcon } from '@neutron/react';
import { RootState } from '../../../redux/store';

/**
 * Internal Imports
 */
import ExportCsvBtn from '../exportCsvBtn';
import DownloadPdfBtn from '../../shared/buttons/DownloadPdfBtn/DownloadPdfBtn';

/**
 * Global Type Definition Imports
 */

/**
 * Style Imports
 */
import {
  DownloadLoadingSpinner,
  DownloadOptionsContainer
} from '../../shared/buttons/DownloadBtn/DownloadBtn.styles';

interface IDownloadExportCsvBtnProps {
  csvData: any;
  fileNamePrefix: string;
  isLoading: boolean;
  handleExportCsv?: () => void;
  handleClickDownloadPDF?: () => void;
}

const DownloadExportCsvBtn: FC<IDownloadExportCsvBtnProps> = ({
  csvData,
  fileNamePrefix,
  isLoading,
  handleExportCsv,
  handleClickDownloadPDF
}) => {
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    // close the popup if the user clicks outside of it
    if (dropdownRef.current && !target.closest('.download-btn')) {
      setShowPopUp(false);
    }
    // close the popup when clicking either the Export CSV or Download PDF button
    // added a settimeout to allow the download to start before the popup closes
    if (dropdownButtonRef.current && target.closest('.download-btn__options')) {
      // timeout time may need to be adjusted based on performance
      setTimeout(() => setShowPopUp(false), 7500);
    }
  }, []);

  useEffect(() => {
    // this is to close popup after csv data is loaded if it is not already closed
    if (csvData.length > 0) {
      setTimeout(() => setShowPopUp(false), 100);
    }
    if (showPopUp) {
      document.addEventListener('mousedown', closeDropdown);
    }
    return () => {
      document.removeEventListener('mousedown', closeDropdown);
    };
  }, [showPopUp, closeDropdown, csvData]);

  return (
    <div
      className="download-btn"
      style={{ minWidth: '8rem', display: 'flex', justifyContent: 'center' }}
      ref={dropdownRef}
      data-testid="download-export-csv-btn"
    >
      <NeuButton
        id="Download-Button"
        onClick={() => setShowPopUp(!showPopUp)}
        kind="tertiary"
        data-testid="download-btn"
        size="small"
      >
        <NeuIcon className="mr-2">get_app</NeuIcon>
        Export
      </NeuButton>
      {showPopUp && (
        <DownloadOptionsContainer
          id="Download-Options-Container"
          className="download-btn__options p-1"
          data-testid="download-options-container"
          ref={dropdownButtonRef}
        >
          {isLoading ? (
            <div style={{ height: '80px', width: '100px' }}>
              <DownloadLoadingSpinner kind="spinner" />
            </div>
          ) : (
            <>
              <ExportCsvBtn
                handleExportCsv={handleExportCsv}
                fileNamePrefix={fileNamePrefix}
              />
              <DownloadPdfBtn handleClickDownloadPDF={handleClickDownloadPDF} />
            </>
          )}
        </DownloadOptionsContainer>
      )}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    isLoading: state.AdminReducer.isLoadingCsvExport,
    csvData: state.AdminReducer.dataToCsvExport
  };
};
export default connect(mapReduxStateToProps)(DownloadExportCsvBtn);
